import { SimpleActivity } from "@ac-expo/api-types";
import { BuyableItemWithPricing, useTicketing } from "@ac-expo/hooks";
import {
  ActivityDetailsModal,
  Button,
  cn,
  DialogFooter,
  toast,
  useIsMobile,
} from "@ac-expo/ui-components";
import {
  createPartialCompleteBuyableItem,
  getBuyableItemGroupKey,
  isTicketingAvailable,
  isTicketTypeWithinSalePeriod,
} from "@ac-expo/utils";
import { useUserContext } from "@web-app/src/context/auth/user.context";
import { useCartContext } from "@web-app/src/context/cart.context";
import { SearchStore } from "@web-app/src/services/SearchStore";
import Search from "@web-app/src/shared/Search";
import { format, isAfter } from "date-fns";
import { observer } from "mobx-react-lite";
import { FC, useMemo } from "react";
import ProtectedButton from "../Auth/components/ProtectedButton";
import { SingleItemTicketing } from "../Ticket/SingleItemTicketing";

type DetailsModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  activities: SimpleActivity[];
  isEmbedded?: boolean;
  eventSlug?: string;
};

export const DetailsModal = observer(
  ({ activities, open, setOpen, isEmbedded, eventSlug }: DetailsModalProps) => {
    const DIALOG_SEARCH_ID = "animation-list-search";
    const searchStore = useMemo(() => new SearchStore(), []);

    const onClose = () => {
      setOpen(false);
    };

    if (activities.length === 0) {
      return null;
    }

    const getActivityRegistrationInfo = (activityId: string) => {
      const activity = activities.find((a) => a.id === activityId);
      const availableTicketTypes =
        activity?.ticketTypes.filter(isTicketTypeWithinSalePeriod) ?? [];
      const activityTicketType = availableTicketTypes.at(0);
      const isAvailableOnDisabledTicketing =
        activity &&
        (activity.structureSchedule || (activity.structureId && activity.organiserSchedule));

      const isRegisterable = activity?.event
        ? ((isTicketingAvailable(activity.event) && activity.organiserSchedule) ||
            isAvailableOnDisabledTicketing) &&
          activityTicketType !== undefined
        : false;

      return {
        activity,
        activityTicketType,
        isRegisterable,
      };
    };

    const getActivityCartPart = (activityId: string) => {
      const { activity, isRegisterable } = getActivityRegistrationInfo(activityId);

      if (!activity || !isRegisterable) {
        return null;
      }

      if (isEmbedded && eventSlug) {
        return <EmbeddedContinueButton activityId={activityId} eventSlug={eventSlug} />;
      }

      if (isEmbedded) {
        return null;
      }

      return <ActivityDetailsCartPart activity={activity} close={onClose} />;
    };

    const getRegisterButton = (activityId: string) => {
      const { isRegisterable } = getActivityRegistrationInfo(activityId);

      return isRegisterable ? (
        <ProtectedButton>
          <Button variant={"outline-white"} className="border-blue-500 text-blue-500">
            S&apos;inscrire
          </Button>
        </ProtectedButton>
      ) : null;
    };

    return (
      <ActivityDetailsModal
        open={open}
        setOpen={setOpen}
        activities={activities}
        getActivityCartPart={getActivityCartPart}
        searchBar={<Search id={DIALOG_SEARCH_ID} store={searchStore} />}
        getRegisterButton={getRegisterButton}
        onClose={onClose}
      />
    );
  },
);

type ActivityDetailsCartPartProps = {
  activity: SimpleActivity;
  close: () => void;
};

const ActivityDetailsCartPart: FC<ActivityDetailsCartPartProps> = ({ activity, close }) => {
  const isMobile = useIsMobile();
  const { toggleCart } = useCartContext();
  const { isAuthenticated } = useUserContext();
  const now = format(new Date(), "yyyy-MM-dd");

  const isRegisterEnabled =
    isTicketingAvailable(activity?.event) ||
    activity.schedules.some((s) => isAfter(s.timetable.day, now));

  const ticketTypes = useMemo(
    () => (activity?.ticketTypes ?? []).filter(isTicketTypeWithinSalePeriod),
    [activity],
  );
  const activityTicketType = ticketTypes.at(0);

  const availableEntries = useMemo((): BuyableItemWithPricing[] => {
    if (!activityTicketType) return [];
    return [
      {
        buyableItem: createPartialCompleteBuyableItem({
          buyableType: "ticket",
          ticketType: {
            id: activityTicketType.id,
            name: activityTicketType.name,
            quantityLeft: activityTicketType.quantityLeft,
            groupMinSize: activityTicketType.groupMinSize,
            event: activity
              ? { id: activity.eventId, name: activity.event?.name ?? activity.name }
              : undefined,
          },
        }),
        pricing: activityTicketType.pricing,
      },
    ];
  }, [activityTicketType, activity]);

  const { localCart, handleAddToCart, isPending } = useTicketing({
    availableEntries,
    activityTicketTypes: ticketTypes,
    basketType: "store",
    isAuthenticated,
    onAddToCartMutationSuccess: () => {
      toast({
        title: `Billets ajoutés au panier`,
        description: `Les billets ont été ajoutés à votre panier.`,
        duration: 1000,
        position: isMobile ? "top" : "bottom-left",
      });
      close();
      toggleCart(true);
    },
    onAddToCartMutationError: () => {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue",
      });
    },
  });

  const { localPricing } = localCart;
  const total = localPricing.total;
  const discount = localPricing.discount;

  if (!isRegisterEnabled || activityTicketType === undefined) {
    return null;
  }

  const itemKey = getBuyableItemGroupKey({
    buyableType: "ticket",
    ticketType: { id: activityTicketType.id },
  });

  return (
    <>
      <SingleItemTicketing
        entry={{ entryType: activityTicketType, itemKey }}
        localCart={localCart}
      />
      <DialogFooter className="flex w-full flex-row items-center !justify-between border-t border-t-gray-300 pt-6">
        <div className="flex flex-col gap-1">
          <p className="text-2xl font-bold">
            <span
              className={cn(discount > 0 && "text-sm text-gray-500 line-through")}
            >{`${formatPrice(total)}`}</span>
            {discount > 0 && <span>{` ${formatPrice(total - discount)}`}</span>}
          </p>
          <p className="text-sm font-medium text-gray-500">TOTAL</p>
        </div>
        <Button isLoading={isPending} variant="blue" onClick={handleAddToCart}>
          Ajouter au panier
        </Button>
      </DialogFooter>
    </>
  );
};

export const formatPrice = (price: number) => {
  if (price === 0) {
    return "Gratuit";
  }
  return `${price.toFixed(2)}€`;
};

type EmbeddedContinueButtonProps = {
  activityId: string;
  eventSlug: string;
};

const EmbeddedContinueButton: FC<EmbeddedContinueButtonProps> = ({ activityId, eventSlug }) => {
  const handleContinueOnSite = () => {
    const url = `${window.location.origin}/event/${eventSlug}?panel=program&activityId=${activityId}`;
    window.open(url, "_blank");
  };

  return (
    <DialogFooter className="flex w-full flex-row items-center !justify-end border-t border-t-gray-300 pt-6">
      <Button variant="blue" onClick={handleContinueOnSite}>
        Continuer sur Connect&apos;Events
      </Button>
    </DialogFooter>
  );
};
